import React, { Component, ErrorInfo, ReactNode } from "react";
import { useTranslation } from 'react-i18next';
import logoblack from './ui/Logo/logo-black.svg';
import logowhite from './ui/Logo/logo-white.svg';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: ErrorInfo) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Log to external service if needed
    // Sentry.captureException(error, { extra: errorInfo });
  }

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, errorInfo!);
      }

      // Default error UI
      return <ErrorFallback error={error} errorInfo={errorInfo} />;
    }

    return children;
  }
}

// Hook for handling async errors
export function useAsyncError() {
  const [, setError] = React.useState();
  return React.useCallback(
    (error: Error) => {
      setError(() => {
        throw error;
      });
    },
    [setError]
  );
}

// CSS preload error handler
// Error Fallback Component with translations
function ErrorFallback({ error, errorInfo }: { error: Error; errorInfo: ErrorInfo | null }) {
  // We can't use hooks in class components, so we'll use a wrapper
  return (
    <ErrorPageUI
      errorMessage={error.message}
      errorInfo={errorInfo}
      showReloadButton={true}
      showHomeButton={true}
    />
  );
}

interface ErrorPageUIProps {
  title?: string;
  message?: string;
  errorMessage?: string;
  errorInfo?: ErrorInfo | null;
  showReloadButton?: boolean;
  showHomeButton?: boolean;
  is404?: boolean;
}

export function ErrorPageUI({
  title,
  message,
  errorMessage,
  errorInfo,
  showReloadButton = true,
  showHomeButton = true,
  is404 = false,
}: ErrorPageUIProps) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = React.useState(false);

  let displayTitle: string;
  let displayMessage: string;

  if (title) {
    displayTitle = title;
  } else if (is404) {
    displayTitle = t('errors.page_not_found', 'Page Not Found');
  } else {
    displayTitle = t('errors.something_went_wrong', 'Something went wrong');
  }

  if (message) {
    displayMessage = message;
  } else if (is404) {
    displayMessage = t('errors.page_not_found_message', "The page you're looking for doesn't exist.");
  } else {
    displayMessage = t('errors.unexpected_error_message', "We're sorry, but something unexpected happened.");
  }

  // Detect dark mode from localStorage or system preference
  const isDark = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    const storedTheme = localStorage.getItem('theme-preference');
    if (storedTheme === 'dark') return true;
    if (storedTheme === 'light') return false;
    if (storedTheme === 'system' || !storedTheme) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        background: isDark
          ? 'linear-gradient(180deg, #111827 0%, #1f2937 100%)'
          : 'linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%)',
        padding: '32px',
        boxSizing: 'border-box',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
        }}
      >
        {/* Logo at top */}
        <div style={{ marginBottom: '48px' }}>
          <img
            src={isDark ? logowhite : logoblack}
            alt="Transcriptor"
            style={{ height: '40px', display: 'block', margin: '0 auto' }}
          />
        </div>

        {/* Animated error icon */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: isDark
              ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(249, 115, 22, 0.3) 100%)'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(249, 115, 22, 0.2) 100%)',
            boxShadow: '0 8px 32px rgba(239, 68, 68, 0.2)',
            marginBottom: '24px',
          }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h1 style={{
          fontSize: '24px',
          fontWeight: 600,
          color: isDark ? '#f9fafb' : '#111827',
          margin: '0 0 8px',
        }}>
          {displayTitle}
        </h1>

        <p style={{
          margin: '0 0 32px',
          fontSize: '16px',
          color: isDark ? '#9ca3af' : '#6b7280',
          lineHeight: 1.5,
        }}>
          {displayMessage}
        </p>

        {/* Error message card */}
        {errorMessage && (
          <div
            style={{
              marginBottom: '32px',
              padding: '16px',
              borderRadius: '8px',
              background: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)'}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <span
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                textAlign: 'left',
                wordBreak: 'break-word',
                fontSize: '14px',
                color: '#ef4444',
              }}
            >
              {errorMessage}
            </span>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '24px' }}>
          {showReloadButton && (
            <button
              onClick={() => window.location.reload()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '140px',
                padding: '12px 20px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: '8px' }}
              >
                <path d="M21 2v6h-6" />
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                <path d="M3 22v-6h6" />
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              </svg>
              {t('errors.reload_page', 'Reload Page')}
            </button>
          )}
          {showHomeButton && (
            <button
              onClick={() => window.location.href = '/'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '140px',
                padding: '12px 20px',
                backgroundColor: isDark ? '#374151' : '#e5e7eb',
                color: isDark ? '#f9fafb' : '#374151',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: '8px' }}
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              {t('errors.go_home', 'Go Home')}
            </button>
          )}
        </div>

        {/* Development details */}
        {process.env.NODE_ENV === "development" && errorInfo && (
          <div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'none',
                border: 'none',
                fontSize: '12px',
                cursor: 'pointer',
                color: isDark ? '#9ca3af' : '#6b7280',
                padding: '4px 8px',
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  marginRight: '6px',
                  transform: showDetails ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
              {t('errors.dev_details', 'Developer Details')}
            </button>

            {showDetails && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '12px',
                  borderRadius: '8px',
                  textAlign: 'left',
                  background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '200px',
                    overflow: 'auto',
                    fontSize: '12px',
                    color: isDark ? '#9ca3af' : '#6b7280',
                  }}
                >
                  {errorInfo.componentStack}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function setupCSSPreloadErrorHandler() {
  if (typeof window !== "undefined") {
    // Handle CSS preload errors
    window.addEventListener("error", (event) => {
      if (
        event.target instanceof HTMLLinkElement &&
        event.target.rel === "preload" &&
        event.target.as === "style"
      ) {
        console.error("CSS preload failed:", event.target.href);
        // Optionally retry loading without preload
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = event.target.href;
        document.head.appendChild(link);
      }
    }, true);

    // Handle unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      console.error("Unhandled promise rejection:", event.reason);
    });
  }
}
